
import { useEffect, useMemo, useState } from 'react';
import AccessDenied from '../components/AccessDenied';
import { API_BASE_URL } from '../config';
import { usePermissions } from '../context/PermissionsContext';
import { useTenant } from '../context/TenantContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import './UserManagement.css';

function UserManagementPage() {
  const { hasRole, permissions, isPermissionsLoading, roles } = usePermissions();
  const { currentTenant } = useTenant();
  const token = localStorage.getItem('token') || '';
  
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successToastMessage, setSuccessToastMessage] = useState('Saved successfully');
  
  // Table state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());

  const roleNameById = useMemo(() => {
    return availableRoles.reduce((acc, role) => {
      acc[role.id] = role.role_name;
      return acc;
    }, {});
  }, [availableRoles]);

  // Fetch current user ID from roles/permissions context or token
  useEffect(() => {
    // Try to get current user ID from token or localStorage
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUserId(payload.userId || payload.id || null);
    } catch (err) {
      // If we can't decode token, we'll handle self-protection differently
      console.warn('Could not decode token for user ID:', err);
    }
  }, [token]);

  // Fetch all users and available roles
  useEffect(() => {
    const fetchUsersAndRoles = async () => {
      if (!hasRole('Admin')) {
        setError('Admin access required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const usersUrl = currentTenant?.id
          ? `${API_BASE_URL}/admin/users?tenant_id=${currentTenant.id}`
          : `${API_BASE_URL}/admin/users`;

        const [usersResponse, rolesResponse] = await Promise.all([
          fetchWithAuth(usersUrl),
          fetchWithAuth(`${API_BASE_URL}/admin/roles`),
        ]);

        if (!usersResponse.ok) {
          throw new Error(`Failed to fetch users: ${usersResponse.status}`);
        }
        if (!rolesResponse.ok) {
          throw new Error(`Failed to fetch roles: ${rolesResponse.status}`);
        }

        const [usersData, rolesData] = await Promise.all([
          usersResponse.json(),
          rolesResponse.json(),
        ]);
        setUsers(usersData);
        setAvailableRoles(Array.isArray(rolesData) ? rolesData : []);
      } catch (err) {
        setError(err.message || 'Failed to load users');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token && !isPermissionsLoading) {
      fetchUsersAndRoles();
    }
  }, [token, hasRole, isPermissionsLoading, currentTenant]);

  // Update user role
    const handleRoleChange = async (userId, newRole) => {
      // Prevent users from changing their own role
      if (userId === currentUserId) {
        alert('You cannot change your own role. Please ask another administrator to do this.');
        return;
      }

      if (!newRole) {
        setError('Please select a role');
        return;
      }
  
      setUpdatingUserId(userId);
      
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/role`, {
          method: 'PUT',
          body: JSON.stringify({ application_role_id: newRole, tenant_id: currentTenant?.id }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 404 && errorData.error?.toLowerCase().includes('flow application')) {
            throw new Error('This user has not been added to the Flow application yet.');
          }
          throw new Error(errorData.error || 'Failed to update role');
        }
  
        await response.json();

        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId
              ? { ...user, role_id: newRole, role_name: roleNameById[newRole] || user.role_name }
              : user
          )
        );
        
        // Show success toast
        setSuccessToastMessage('Role updated successfully!');
        setShowSuccessToast(true);
        
        // Hide toast after 3 seconds
        setTimeout(() => {
          setShowSuccessToast(false);
        }, 3000);
      } catch (err) {
        setError(err.message || 'Failed to update user role');
        console.error('Error updating user role:', err);
      } finally {
        setUpdatingUserId(null);
      }
    };

  // Sorting function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = users.filter(user =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle role name sorting
        if (sortConfig.key === 'role_name') {
          aValue = a.role_name || 'No Role';
          bValue = b.role_name || 'No Role';
        }
        
        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Convert to strings for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [users, searchTerm, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  // Handle row selection
  const toggleRowSelection = (userId) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(userId)) {
      newSelectedRows.delete(userId);
    } else {
      newSelectedRows.add(userId);
    }
    setSelectedRows(newSelectedRows);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === paginatedUsers.length) {
      setSelectedRows(new Set());
    } else {
      const newSelectedRows = new Set();
      paginatedUsers.forEach(user => newSelectedRows.add(user.id));
      setSelectedRows(newSelectedRows);
    }
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (!hasRole('Admin')) {
    return (
      <AccessDenied section="User Management" role="Admin" />
    );
  }

  return (
       <div className="user-management-container">
         <div className="user-management-header">
           <h1>User Management</h1>
           <p>Manage user roles and permissions</p>
         </div>
         
         {/* Success Toast */}
         {showSuccessToast && (
           <div className="toast-success">
             {successToastMessage}
           </div>
         )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {loading && users.length === 0 ? (
        <div className="alert alert-info">
          Loading users...
        </div>
      ) : (
        <>
          {/* Filter Bar */}
          <div className="table-filter-bar">
            <div className="filter-input">
              <label htmlFor="userSearch">Search Users</label>
              <input
                type="text"
                id="userSearch"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search users"
              />
            </div>
            <div className="filter-count">
              Showing {paginatedUsers.length} of {filteredAndSortedUsers.length} users
              {selectedRows.size > 0 && ` | ${selectedRows.size} selected`}
            </div>
          </div>

          {/* Table Container */}
          <div className="user-table-container">
            <div className="user-table-wrapper">
              <table className="user-table" role="grid">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        className="row-selection-checkbox"
                        checked={paginatedUsers.length > 0 && selectedRows.size === paginatedUsers.length}
                        onChange={toggleAllRows}
                        aria-label="Select all rows"
                      />
                    </th>
                    <th scope="col">
                      <button
                        className="sortable-header"
                        onClick={() => requestSort('name')}
                        aria-label={`Sort by name, currently ${sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'unsorted'}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: 0,
                          fontFamily: 'var(--secondary-font)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Name {getSortIndicator('name')}
                      </button>
                    </th>
                    <th scope="col">
                      <button
                        className="sortable-header"
                        onClick={() => requestSort('email')}
                        aria-label={`Sort by email, currently ${sortConfig.key === 'email' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'unsorted'}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: 0,
                          fontFamily: 'var(--secondary-font)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Email {getSortIndicator('email')}
                      </button>
                    </th>
                    <th scope="col">
                      <button
                        className="sortable-header"
                        onClick={() => requestSort('role_name')}
                        aria-label={`Sort by role, currently ${sortConfig.key === 'role_name' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'unsorted'}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: 0,
                          fontFamily: 'var(--secondary-font)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Role {getSortIndicator('role_name')}
                      </button>
                    </th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map(user => (
                      <tr
                        key={user.id}
                        className={selectedRows.has(user.id) ? 'selected' : ''}
                        tabIndex={0}
                        role="row"
                        aria-selected={selectedRows.has(user.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleRowSelection(user.id);
                          }
                        }}
                      >
                        <td>
                          <input
                            type="checkbox"
                            className="row-selection-checkbox"
                            checked={selectedRows.has(user.id)}
                            onChange={() => toggleRowSelection(user.id)}
                            aria-label={`Select row for ${user.name}`}
                          />
                        </td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-badge role-${user.role_name?.toLowerCase() || 'none'}`}>
                            {user.role_name || 'No Role'}
                          </span>
                        </td>
                        <td>
                          {updatingUserId === user.id ? (
                            <div className="role-updating">
                              <span>Updating...</span>
                            </div>
                          ) : (
                            <div className="role-actions">
                              <select
                                value={user.role_id || ''}
                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                disabled={user.id === currentUserId}
                                aria-label={`Change role for ${user.name}`}
                              >
                                <option value="">Select role</option>
                                {availableRoles.map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {role.role_name}
                                  </option>
                                ))}
                              </select>
                              {user.id === currentUserId && (
                                <span className="self-role-notice">
                                  (Your role)
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="table-empty-cell">
                        <div className="table-empty">
                          <div className="table-empty-icon">
                            <i className="bi bi-people"></i>
                          </div>
                          <h3>No users found</h3>
                          <p>{searchTerm ? 'Try adjusting your search terms' : 'No users available'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredAndSortedUsers.length > itemsPerPage && (
              <div className="table-pagination">
                <div className="pagination-info">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredAndSortedUsers.length)} of{' '}
                  {filteredAndSortedUsers.length} users
                </div>
                <div className="pagination-controls">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    aria-label="Items per page"
                    style={{
                      fontFamily: 'var(--secondary-font)',
                      fontSize: '0.9rem',
                      padding: '0.35rem 0.75rem',
                      border: '1px solid var(--theme-table-border-strong)',
                      borderRadius: 'var(--border-radius-sm)',
                      background: 'var(--theme-table-surface)',
                      color: 'var(--theme-table-text)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <span style={{ fontFamily: 'var(--secondary-font)', fontSize: '0.9rem', color: 'var(--theme-table-text)' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UserManagementPage;
